import React from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight,
  WhatsappLogo,
  CheckCircle,
  Lightning,
  ShieldCheck,
  Package,
} from "@phosphor-icons/react";
import SectionLabel from "../components/SectionLabel";
import { COMPANY, MEDIA, WHY_SIFRA, INDUSTRIES, CLIENTS } from "../data/content";

const Home = () => {
  return (
    <>
      {/* HERO */}
      <section
        className="relative border-b border-[var(--sifra-line)] overflow-hidden"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 py-16 md:py-28 items-center">
          <div className="lg:col-span-7">
            <div className="rise rise-1">
              <SectionLabel testid="hero-label">
                We never stop working for you
              </SectionLabel>
            </div>
            <h1
              className="rise rise-2 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-6"
              data-testid="hero-heading"
            >
              Enterprise IT hardware,
              <br />
              <span className="text-[var(--sifra-ink-mute)]">delivered with</span>{" "}
              precision.
            </h1>
            <p
              className="rise rise-3 mt-6 md:mt-8 text-base md:text-lg text-[var(--sifra-ink-soft)] max-w-xl leading-relaxed"
              data-testid="hero-subheading"
            >
              Sifra Enterprises is a trusted corporate IT partner supplying
              genuine, warrantied hardware and on-site support to leading
              organisations across India — since 2011.
            </p>
            <div className="rise rise-4 mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="inline-flex items-center gap-2 bg-[var(--sifra-ink)] text-white px-7 py-4 text-xs tracking-label font-semibold uppercase hover:bg-[var(--sifra-accent)] transition-colors duration-300"
              >
                Get a Quote <ArrowUpRight size={14} weight="bold" />
              </Link>
              <a
                href={COMPANY.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="inline-flex items-center gap-2 border border-[var(--sifra-ink)] text-[var(--sifra-ink)] px-7 py-4 text-xs tracking-label font-semibold uppercase hover:bg-[var(--sifra-ink)] hover:text-white transition-colors duration-300"
              >
                <WhatsappLogo size={16} weight="fill" /> Chat on WhatsApp
              </a>
            </div>

            <div className="rise rise-5 mt-14 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { n: "14+", l: "Years serving corporates" },
                { n: "14+", l: "Flagship clients" },
                { n: "Pan-India", l: "Delivery & support" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="border-l border-[var(--sifra-line)] pl-4"
                  data-testid={`hero-stat-${i}`}
                >
                  <div className="font-display font-black text-3xl md:text-4xl tracking-tighter whitespace-nowrap">
                    {s.n}
                  </div>
                  <div className="text-xs tracking-label uppercase text-[var(--sifra-ink-mute)] mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] md:aspect-[5/6] border border-[var(--sifra-line)] overflow-hidden bg-[var(--sifra-surface)]">
              <img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
                alt="Blue-lit modern data centre aisle"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHY SIFRA */}
      <section
        className="border-b border-[var(--sifra-line)] bg-white"
        data-testid="why-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-8">
              <SectionLabel>Why Sifra</SectionLabel>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
                We don't just supply hardware.
                <br />
                <span className="text-[var(--sifra-ink-mute)]">
                  We build long-term IT partnerships.
                </span>
              </h2>
            </div>
            <div className="md:col-span-4 text-[var(--sifra-ink-soft)]">
              From dedicated account managers to same-day on-site assistance — every
              detail is engineered to keep your teams productive.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 border border-[var(--sifra-line)]">
            {WHY_SIFRA.map((w, i) => {
              const Icon = [
                ShieldCheck,
                Lightning,
                CheckCircle,
                Package,
                ArrowUpRight,
              ][i];
              return (
                <div
                  key={w.k}
                  data-testid={`why-card-${i}`}
                  className={`p-8 md:p-10 min-h-[220px] flex flex-col justify-between ${
                    i < WHY_SIFRA.length - 1
                      ? "border-b md:border-b-0 md:border-r border-[var(--sifra-line)]"
                      : ""
                  } hover:bg-[var(--sifra-ink)] hover:text-white transition-colors duration-500 group cursor-default`}
                >
                  <Icon
                    size={28}
                    weight="thin"
                    className="text-[var(--sifra-ink)] group-hover:text-white transition-colors"
                  />
                  <div>
                    <div className="font-display font-black text-xl tracking-tighter mb-2">
                      {w.k}
                    </div>
                    <div className="text-sm text-[var(--sifra-ink-soft)] group-hover:text-white/70 leading-relaxed">
                      {w.v}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES MARQUEE */}
      <section
        className="bg-[var(--sifra-ink)] text-white border-b border-[var(--sifra-line-dark)] overflow-hidden"
        data-testid="industries-marquee"
      >
        <div className="py-10 md:py-14">
          <Marquee speed={45} gradient={false} pauseOnHover>
            {[...INDUSTRIES, ...INDUSTRIES].map((ind, i) => (
              <span
                key={i}
                className="font-display font-black text-4xl md:text-6xl tracking-tighter px-8 md:px-14 flex items-center gap-8"
              >
                {ind}
                <span className="text-[var(--sifra-accent)]">●</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* CLIENTS PREVIEW */}
      <section
        className="border-b border-[var(--sifra-line)] bg-white"
        data-testid="clients-preview"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-8">
              <SectionLabel>Trusted by</SectionLabel>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
                Leading organisations across India.
              </h2>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/clients"
                data-testid="home-clients-link"
                className="inline-flex items-center gap-2 text-xs tracking-label uppercase font-semibold link-underline"
              >
                View all clients <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 border border-[var(--sifra-line)]">
            {CLIENTS.slice(0, 7).map((c, i) => (
              <div
                key={c.name}
                data-testid={`client-preview-${i}`}
                className={`aspect-square flex items-center justify-center p-5 bg-white group hover:bg-[var(--sifra-ink)] transition-colors duration-500 relative overflow-hidden ${
                  i < 6 ? "border-r border-b md:border-b-0 border-[var(--sifra-line)]" : "border-b md:border-b-0"
                }`}
                title={c.name}
              >
                {c.logo ? (
                  <img
                    src={c.logo}
                    alt={`${c.name} logo`}
                    loading="lazy"
                    className="max-w-[80%] max-h-[70%] object-contain group-hover:opacity-0 transition-all duration-500"
                  />
                ) : (
                  <div className="font-display font-black text-3xl tracking-tighter text-[var(--sifra-ink)] group-hover:opacity-0 transition-opacity duration-500">
                    {c.monogram}
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center font-display font-black text-xl md:text-2xl tracking-tighter text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-center px-3">
                  {c.monogram}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="border border-[var(--sifra-ink)] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <SectionLabel>Ready when you are</SectionLabel>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
                Share your IT requirement — get a quote within hours.
              </h2>
            </div>
            <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="inline-flex items-center gap-2 bg-[var(--sifra-ink)] text-white px-6 py-4 text-xs tracking-label font-semibold uppercase hover:bg-[var(--sifra-accent)] transition-colors"
              >
                Request Quote <ArrowUpRight size={14} weight="bold" />
              </Link>
              <a
                href={COMPANY.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-cta-whatsapp"
                className="inline-flex items-center gap-2 border border-[var(--sifra-ink)] px-6 py-4 text-xs tracking-label font-semibold uppercase hover:bg-[var(--sifra-ink)] hover:text-white transition-colors"
              >
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
